import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import container from 'src/container';
import {
    EventAudienceCounts as AudienceCounts,
    EventType,
} from 'src/domain/event';
import { EventAudienceCounts } from './EventAudienceCounts';

const {
    cradle: { munduClient },
} = container;

export const EventAudienceSections = ({
    organizationId,
    eventType,
    sendToAll,
    groupIds,
    consumerIds,
    attendees,
    replyOption,
    isOccurrenceEdit,
    occurrenceCounts,
}: {
    organizationId: string;
    eventType: EventType;
    sendToAll: boolean;
    groupIds: string[];
    consumerIds: string[];
    attendees: ReactNode;
    replyOption?: ReactNode;
    isOccurrenceEdit: boolean;
    occurrenceCounts?: Partial<AudienceCounts>;
}) => {
    const { t } = useTranslation();
    const [counts, setCounts] = useState<Partial<AudienceCounts>>();
    const [isLoading, setIsLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const groupIdsKey = groupIds.join(',');
    const consumerIdsKey = consumerIds.join(',');
    const hasAudience = sendToAll || Boolean(groupIdsKey || consumerIdsKey);

    const previewEnabled =
        !isOccurrenceEdit && eventType !== EventType.internal;

    useEffect(() => {
        let current = true;
        setCounts(undefined);
        setFailed(false);
        if (!organizationId || !hasAudience || !previewEnabled) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        munduClient
            .previewEventInvitees({
                organizationId,
                sendToAll,
                groupIds: groupIdsKey ? groupIdsKey.split(',') : [],
                consumerIds: consumerIdsKey ? consumerIdsKey.split(',') : [],
            })
            .then(result => {
                if (current)
                    setCounts({
                        residentCount: result.residentCount,
                        relativeCount: result.relativeCount,
                        mainContactCount: result.mainContactCount,
                    });
            })
            .catch(() => {
                if (current) setFailed(true);
            })
            .finally(() => {
                if (current) setIsLoading(false);
            });
        return () => {
            current = false;
        };
    }, [
        organizationId,
        hasAudience,
        sendToAll,
        groupIdsKey,
        consumerIdsKey,
        previewEnabled,
    ]);

    const displayedCounts = isOccurrenceEdit ? occurrenceCounts : counts;
    const loading = !isOccurrenceEdit && (isLoading || (!counts && !failed));
    const showCounts =
        eventType !== EventType.internal && (isOccurrenceEdit || hasAudience);

    return (
        <div className="space-y-4">
            <section
                className={`space-y-4 rounded-2xl p-4 transition-colors sm:p-5 ${
                    eventType === EventType.internal
                        ? 'bg-vanillaYellow'
                        : 'bg-white bg-opacity-50'
                }`}
            >
                <h3 className="font-semibold">
                    {t('modules.calendar.form.attendeesLabel')}
                </h3>
                {attendees}
                {showCounts ? (
                    <EventAudienceCounts
                        counts={displayedCounts}
                        eventType={eventType}
                        audience="residents"
                        isLoading={loading}
                    />
                ) : null}
                {failed && (
                    <p className="text-sm">
                        {t('modules.calendar.form.audiencePreviewError')}
                    </p>
                )}
            </section>
            {eventType === EventType.relatives && replyOption ? (
                <section className="space-y-4 rounded-2xl bg-white bg-opacity-50 p-4 sm:p-5">
                    <h3 className="font-semibold">
                        {t('modules.calendar.form.requestReplyTitle')}
                    </h3>
                    {replyOption}
                    {showCounts ? (
                        <EventAudienceCounts
                            counts={displayedCounts}
                            eventType={eventType}
                            audience="mainContacts"
                            descriptive
                            isLoading={loading}
                        />
                    ) : null}
                </section>
            ) : null}
        </div>
    );
};
